import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, googleProvider } from '../services/firebase';
import {
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  getAdditionalUserInfo,
} from 'firebase/auth';
import logo from '../assets/images/Cognidoor Logo.png';
import useAuthUser from '../services/useAuthUser';
import { getAttributionSnapshot } from '../lib/attribution';
import { syncAttributionTouch } from '../lib/attribution/syncTouch';
import { setAnalyticsUserId, trackSignUp } from '../lib/analytics/gtag';

const actionCodeSettings = {
  url: window.location.origin + '/auth',
  handleCodeInApp: true,
};

async function handlePostAuth(user, { isSignUp, method }) {
  setAnalyticsUserId(user.uid);
  const touchType = isSignUp ? 'signup' : 'login';
  await syncAttributionTouch(user, touchType);
  if (isSignUp) {
    trackSignUp(method, getAttributionSnapshot());
  }
}

export default function AuthPage() {
  const [authMode, setAuthMode] = useState('password');
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const plan = searchParams.get('plan');
  const redirect = searchParams.get('redirect');
  const { user, loading: authLoading } = useAuthUser();

  const getPostAuthDestination = useCallback(() => {
    if (redirect) return redirect;
    if (plan === 'basic') return '/checkout/basic';
    if (plan === 'pro') return '/checkout/pro';
    return '/';
  }, [redirect, plan]);

  useEffect(() => {
    if (!authLoading && user) {
      navigate(getPostAuthDestination(), { replace: true });
    }
  }, [user, authLoading, navigate, getPostAuthDestination]);

  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      setAuthMode('link');
      setInfo('Completing sign-in...');
      setLoading(true);
      let storedEmail = window.localStorage.getItem('emailForSignIn') || '';
      if (!storedEmail) {
        storedEmail = window.prompt('Please provide your email for confirmation');
      }
      if (storedEmail) {
        signInWithEmailLink(auth, storedEmail, window.location.href)
          .then(async (credential) => {
            window.localStorage.removeItem('emailForSignIn');
            const isNewUser = Boolean(getAdditionalUserInfo(credential)?.isNewUser);
            await handlePostAuth(credential.user, {
              isSignUp: isNewUser,
              method: 'link',
            });
            setInfo('Sign-in successful! Redirecting...');
            setTimeout(() => {
              navigate(getPostAuthDestination(), { replace: true });
            }, 1200);
          })
          .catch((err) => {
            setError(err.message);
            setLoading(false);
          });
      } else {
        setError('Email is required to complete sign-in.');
        setLoading(false);
      }
    }
  }, [navigate, getPostAuthDestination]);

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const isNewUser = Boolean(getAdditionalUserInfo(result)?.isNewUser);
      await handlePostAuth(result.user, { isSignUp: isNewUser, method: 'google' });
      navigate(getPostAuthDestination(), { replace: true });
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isSignUp) {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        await handlePostAuth(credential.user, { isSignUp: true, method: 'email' });
      } else {
        const credential = await signInWithEmailAndPassword(auth, email, password);
        await handlePostAuth(credential.user, { isSignUp: false, method: 'email' });
      }
      navigate(getPostAuthDestination(), { replace: true });
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleSendLink = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      setLinkSent(true);
      setInfo('Sign-in link sent! Check your email.');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (authLoading || user) return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-white via-blue-50 to-blue-100 px-2 sm:px-4">
      <div className="w-full max-w-xs sm:max-w-md bg-white rounded-2xl shadow-xl p-4 sm:p-8 flex flex-col items-center">
        <img src={logo} alt="Cognidoor Logo" className="w-32 sm:w-48 mb-4 sm:mb-6" />
        <div className="flex w-full mb-4 sm:mb-6">
          <button
            className={`flex-1 py-2 rounded-l-lg font-semibold text-xs sm:text-base transition-all ${authMode === 'password' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            onClick={() => { setAuthMode('password'); setError(''); setInfo(''); }}
            disabled={loading}
          >
            Email/Password
          </button>
          <button
            className={`flex-1 py-2 sm:py-3 rounded-r-lg font-semibold text-xs sm:text-base transition-all ${authMode === 'link' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            onClick={() => { setAuthMode('link'); setError(''); setInfo(''); }}
            disabled={loading}
          >
            <div className="flex flex-col items-center leading-tight">
              <span className="text-xs sm:text-sm">Passwordless</span>
              <span className="text-[9px] sm:text-[10px] mt-0.5">(Email Link)</span>
            </div>
          </button>
        </div>
        <button
          onClick={handleGoogleLogin}
          className="w-full flex items-center justify-center gap-3 py-2 sm:py-3 mb-3 sm:mb-5 rounded-lg bg-white border border-gray-200 shadow hover:shadow-md transition-all font-semibold text-gray-700 hover:bg-blue-50 text-xs sm:text-base"
          disabled={loading}
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" /> Continue with Google
        </button>
        <div className="flex items-center w-full my-3 sm:my-4">
          <div className="flex-grow h-px bg-gray-200" />
          <span className="mx-2 sm:mx-3 text-gray-400 text-xs sm:text-sm">or</span>
          <div className="flex-grow h-px bg-gray-200" />
        </div>
        {authMode === 'password' ? (
          <form onSubmit={handleEmailAuth} className="w-full flex flex-col gap-3 sm:gap-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-200 focus:border-blue-400 focus:outline-none bg-gray-50 text-xs sm:text-base"
              required
              autoComplete="email"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-200 focus:border-blue-400 focus:outline-none bg-gray-50 text-xs sm:text-base"
              required
              autoComplete={isSignUp ? "new-password" : "current-password"}
            />
            {error && <div className="text-red-500 text-xs sm:text-sm text-center">{error}</div>}
            <button
              type="submit"
              className="w-full py-2 sm:py-3 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors mt-1 sm:mt-2 text-xs sm:text-base"
              disabled={loading}
            >
              {loading ? (isSignUp ? 'Signing Up...' : 'Logging In...') : (isSignUp ? 'Sign Up' : 'Log In')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSendLink} className="w-full flex flex-col gap-3 sm:gap-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-200 focus:border-blue-400 focus:outline-none bg-gray-50 text-xs sm:text-base"
              required
              autoComplete="email"
              disabled={linkSent}
            />
            {error && <div className="text-red-500 text-xs sm:text-sm text-center">{error}</div>}
            {info && <div className="text-green-600 text-xs sm:text-sm text-center">{info}</div>}
            <button
              type="submit"
              className="w-full py-2 sm:py-3 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors mt-1 sm:mt-2 text-xs sm:text-base"
              disabled={loading || linkSent}
            >
              {loading ? 'Sending Link...' : (linkSent ? 'Link Sent!' : 'Send Sign-In Link')}
            </button>
          </form>
        )}
        {authMode === 'password' && (
          <div className="mt-4 sm:mt-6 text-xs sm:text-sm text-gray-600">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}
            <button
              className="ml-2 text-blue-600 hover:underline font-semibold"
              onClick={() => setIsSignUp(!isSignUp)}
              disabled={loading}
            >
              {isSignUp ? 'Log In' : 'Sign Up'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
